import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  number,
  oneOfType,
  string,
  object,
  array,
  bool,
  func,
} from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { formatShortDate } from '@airasia-common/libraries/datetime';
import Styled from '../../utils/Styled';
import Image from '../Image';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import { BodyText, CaptionText } from '../Text/Heading';
import { AmenitiesList, AmenitiesType } from '../Amenity';

const CardContainer = Styled(Wrapper, {
  borderRadius: 4,
  bg: 'white',
  py: '12px',
  px: 4,
});

const HrLine = styled.hr`
  width: 100%;
  color: ${({ theme }) => theme.colors.greyLight};
`;

const SummaryWrapper = Styled(Wrapper, {
  display: 'block',
});

const ImageWrapper = Styled(Wrapper, {
  flex: 1,
  borderRadius: '4px',
});

const InfoRightColumn = Styled(Wrapper, {
  width: '100%',
  pl: 4,
  alignItems: 'flex-end',
  textAlign: 'left',
});
const AmenitesWrapper = Styled(Wrapper, {
  clear: 'both',
  width: '100%',
  pl: 0,
  display: 'flex',
  flexWrap: 'nowrap',
});

const HighLightSpan = styled.span`
  color: ${({ theme, move }) =>
    move ? theme.colors.secondaryMove : theme.colors.secondary};
`;

const HighLightCaptionText = styled(CaptionText)`
  display: flex;
  flex-wrap: wrap;
`;

const BogoCaptionText = styled(CaptionText)`
  background-color: ${({ theme, isRM9PromoEnabled }) =>
    isRM9PromoEnabled ? '#ad1c29' : theme.colors.primary};
  padding: 4px 8px;
  border-radius: 5px;
  display: block;
  width: fit-content;
  margin-top: 8px;
  line-height: 18px;
  font-weight: 500;
  color: white;
`;
const CorporateRateTag = styled(CaptionText)`
  background-color: #f2f5fc;
  padding: 4px 8px;
  border-radius: 5px;
  display: block;
  width: fit-content;
  margin-top: 8px;
  font-weight: 500;
  color: #2d3a6f;
  font-family: DM Sans;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
`;

export const HotelSummary = ({
  imageUrl,
  hotelName,
  fromDate,
  toDate,
  roomsDescription,
  roomName,
  roomsCount: roomsCountText,
  totalNights,
  className,
  imageStyle,
  isBogoPromoEnabled,
  discountId,
  hotelNameStyle,
  dateRangeStyle,
  roomsDescriptionStyle,
  amenities,
  showDevive,
  viewAmenitiesType,
  onImageLoad,
  move,
  isRM9PromoEnabled,
  rate,
  hotelContent,
  isShowHotelContent,
}) => {
  const [AmenitiesDisplayType, setAmenitiesDisplayType] = useState(
    AmenitiesType.ONE_COLUMN_FULL_WIDTH
  );

  useEffect(() => {
    if (viewAmenitiesType && viewAmenitiesType !== AmenitiesDisplayType) {
      if (Object.values(AmenitiesType).indexOf(viewAmenitiesType) !== -1) {
        setAmenitiesDisplayType(viewAmenitiesType);
      }
    }
  }, [AmenitiesDisplayType, viewAmenitiesType]);

  const address = hotelContent?.address;
  const fullAddress = [
    address?.line_1,
    address?.line_2,
    address?.city !== address?.state_province_name ? address.city : null,
    address?.postal_code,
    address?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const normalizeNumber = number => {
    if (!number) return null;
    return number.startsWith('+') ? number : `+${number}`;
  };

  const phone = normalizeNumber(hotelContent?.phone);

  let contactInfo = null;
  if (phone) {
    contactInfo = phone;
  } else {
    contactInfo = null;
  }

  return (
    <>
      <SummaryWrapper className={className}>
        <Row margin={1}>
          <ImageWrapper>
            {imageUrl && (
              <Image
                src={imageUrl}
                height={68}
                width={68}
                borderRadius="4px"
                className="hotel-summary-image"
                onLoad={onImageLoad}
                {...imageStyle}
              />
            )}
          </ImageWrapper>
          <InfoRightColumn>
            <BodyText
              className="hotel-name"
              mt={2}
              fontSize={14}
              lineHeight={'20px'}
              numberOfLines={2}
              {...hotelNameStyle}
            >
              {hotelName}
            </BodyText>

            <CaptionText
              className="booking-summary"
              mt={2}
              lineHeight={'18px'}
              fontWeight="500"
              numberOfLines={2}
              {...dateRangeStyle}
            >
              {translate('tripSummary.totalNights', {
                fromDate: formatShortDate(fromDate),
                toDate: formatShortDate(toDate),
                smart_count: +totalNights,
              })}
            </CaptionText>

            {roomsDescription ? (
              <CaptionText
                mt={2}
                className="room-descriptions"
                lineHeight={'18px'}
                {...roomsDescriptionStyle}
              >
                {roomsCountText > 1 ? `${roomsCountText} ` : ''}
                {roomsDescription}
              </CaptionText>
            ) : (
              roomsCountText &&
              roomName && (
                <HighLightCaptionText
                  mt={2}
                  className="room-descriptions"
                  lineHeight={'18px'}
                  {...roomsDescriptionStyle}
                >
                  <Wrapper className="room-name">{roomName}&nbsp;</Wrapper>
                  <Wrapper flexGrow={1}>
                    <HighLightSpan className="room-count" move={move}>
                      ({roomsCountText.toLowerCase()})
                    </HighLightSpan>
                  </Wrapper>
                </HighLightCaptionText>
              )
            )}

            {isBogoPromoEnabled && (
              <BogoCaptionText
                className="booking-summary"
                isRM9PromoEnabled={isRM9PromoEnabled}
              >
                {isRM9PromoEnabled
                  ? translate(`hotelCouponDiscount.RM9.listing.tag`, {
                      promo:
                        rate?.second_night?.tag?.currency +
                        rate?.second_night?.tag?.value,
                    })
                  : isBogoPromoEnabled
                  ? translate(`hotelCouponDiscount.${discountId}.listing.title`)
                  : null}
              </BogoCaptionText>
            )}
            {rate?.isDutyTravelRate && (
              <CorporateRateTag className="booking-summary">
                {translate(`common.dutyTravelRate`)}
              </CorporateRateTag>
            )}
            {rate?.isStaffTravel && (
              <CorporateRateTag className="booking-summary">
                {translate(`common.staff_travel_rate`)}
              </CorporateRateTag>
            )}

            {isShowHotelContent && fullAddress && (
              <div style={{ width: '90%', maxWidth: '600px' }}>
                <CaptionText
                  className="booking-summary"
                  lineHeight={'18px'}
                  multiLine={true}
                  numberOfLines={2}
                  {...roomsDescriptionStyle}
                >
                  {fullAddress}
                </CaptionText>
              </div>
            )}
            {isShowHotelContent && contactInfo && (
              <CaptionText
                className="booking-summary"
                lineHeight={'18px'}
                {...roomsDescriptionStyle}
              >
                {contactInfo}
              </CaptionText>
            )}
          </InfoRightColumn>
        </Row>
        {amenities && amenities.length > 0 && (
          <>
            {showDevive && (
              <Row>
                <HrLine></HrLine>
              </Row>
            )}
            <Row>
              <AmenitesWrapper>
                <AmenitiesList
                  amenities={amenities}
                  size={15}
                  type={AmenitiesDisplayType}
                  move={move}
                ></AmenitiesList>
              </AmenitesWrapper>
            </Row>
          </>
        )}
      </SummaryWrapper>
    </>
  );
};

HotelSummary.propTypes = {
  imageUrl: string,
  hotelName: string.isRequired,
  fromDate: string.isRequired,
  toDate: string.isRequired,
  roomsDescription: string,
  roomName: string,
  totalNights: oneOfType([string, number]).isRequired,
  className: string,
  imageStyle: object,
  hotelNameStyle: object,
  dateRangeStyle: object,
  isBogoPromoEnabled: bool,
  discountId: string,
  roomsDescriptionStyle: object,
  roomsCount: oneOfType([string, number]),
  amenities: array,
  showDevive: bool,
  viewAmenitiesType: string,
  onImageLoad: func,
  move: bool,
  isRM9PromoEnabled: bool,
  rate: object,
  hotelContent: object,
  isShowHotelContent: bool,
};

const HotelCardSummary = props => {
  return (
    <CardContainer>
      <HotelSummary {...props} />
    </CardContainer>
  );
};

export default HotelCardSummary;
